import {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {usePath} from 'hooks';
import {PATHS} from 'routes/constants';
import {useSummary} from 'context/summary';
import {useIVStore} from '@chekin/shared';
import {ensureIVIntent} from 'hooks/iv/ivIntent';
import {getFeatureFlags, QUERY_CACHE_KEYS} from '@chekin/core';

export const useRemoteIVFlow = (isNeedContactVerification?: boolean) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {reservationId} = useSummary();
  const {getFullPath, getMainPath} = usePath();
  const {initIVState} = useIVStore();
  const isPropertyQRScanned = useIVStore(state => state.isPropertyQRScanned);
  const getStartVerificationPath = useCallback(
    (isContactVerification?: boolean) =>
      isContactVerification
        ? PATHS.verification.contacts
        : PATHS.verification.documentSelection,
    [],
  );

  const goNextAfterContactVerification = useCallback(() => {
    const path = getStartVerificationPath(false);
    navigate(getFullPath(path));
  }, [navigate, getStartVerificationPath, getFullPath]);

  const goToGuestForm = useCallback(() => {
    navigate(getFullPath(PATHS.addPersonalDataForm));
  }, [navigate, getFullPath]);

  const goToGuestRegistration = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [QUERY_CACHE_KEYS.guestGroup],
    });
    navigate(getMainPath(PATHS.guestRegistration));
  }, [queryClient, navigate, getMainPath]);

  const goToHome = useCallback(() => {
    navigate(getFullPath(PATHS.home));
  }, [navigate, getFullPath]);

  const startVerification = useCallback(async () => {
    try {
      if (getFeatureFlags('IVCO_49_MOVE_IV_TO_MOBILE')) {
        await ensureIVIntent({reservationId, isPropertyQRScanned});
      }
    } catch (error) {
      console.error(error);
    }

    initIVState();
    navigate(getStartVerificationPath(isNeedContactVerification));
  }, [
    getStartVerificationPath,
    navigate,
    reservationId,
    initIVState,
    isPropertyQRScanned,
    isNeedContactVerification,
  ]);

  return {
    startVerification,
    goToGuestForm,
    goToHome,
    goToGuestRegistration,
    goNextAfterContactVerification,
  };
};
